import * as Sentry from '@sentry/react';

import env from '../Config/env';

/**
 * Inicializa o Sentry se a DSN estiver configurada. Caso contrário, fica off
 * silenciosamente — útil para dev local sem poluir o projeto Sentry e para
 * permitir build em ambientes sem a variável definida.
 *
 * Idempotente: pode ser chamado mais de uma vez sem efeito colateral.
 *
 * Origem: TD-008 Onda 1 (error tracking). O plano completo está em
 * .context/plans/2026-06-07-td-008-sentry-onda-1.md.
 */

let initialized = false;

export const initSentry = () => {
	if (initialized) return;
	if (!env.SENTRY_DSN) return;

	Sentry.init({
		dsn: env.SENTRY_DSN,
		environment: env.APP_ENV,
		// Performance/replay desligados — fica dentro do plano free e evita overhead.
		// Ligar depois (Onda 2 do TD-008) com tracesSampleRate ~0.1.
		tracesSampleRate: 0,
		replaysSessionSampleRate: 0,
		replaysOnErrorSampleRate: 0,
		// Erros conhecidos de cancelamento (AbortController) — esperados pelo padrão
		// de useAbortController do TD-020/TD-064; não são bugs, ignorar.
		ignoreErrors: ['AbortError', 'CanceledError'],
	});

	initialized = true;
};

const isInitialized = () => initialized;

/**
 * Captura uma exceção. O segundo parâmetro vira "extra" na issue do Sentry
 * (limite ~16KB por chave, então mantém compacto).
 */
export const captureError = (error, context = {}) => {
	if (!initialized) return;
	const err = (error instanceof Error) ? error : new Error(String(error));
	Sentry.captureException(err, { extra: context });
};

/**
 * Mensagem livre (não-exception). level aceita 'fatal'|'error'|'warning'|'log'
 * |'info'|'debug'. Use para sinalizar eventos relevantes sem stack trace.
 */
export const captureMessage = (message, level = 'info', context = {}) => {
	if (!initialized) return;
	Sentry.captureMessage(message, { level, extra: context });
};

/**
 * Identifica o usuário logado nas issues. Chamar após login bem-sucedido
 * (com dados do JWT) e limpar no logout via clearUser().
 *
 * @param {{id?: string|number, email?: string, username?: string}} user
 */
export const setUser = (user) => {
	if (!initialized) return;
	Sentry.setUser(user);
};

export const clearUser = () => {
	if (!initialized) return;
	Sentry.setUser(null);
};

/**
 * Define uma tag no escopo global do Sentry. Tags são filtros first-class
 * no dashboard (sidebar Filters). Útil pra categorizar issues por
 * dimensões de negócio: agência, perfil de usuário, etc.
 *
 * Custo: zero (não conta como event).
 */
export const setTag = (key, value) => {
	if (!initialized) return;
	Sentry.setTag(key, value);
};

/**
 * Adiciona breadcrumb manual — aparece no trilho antes de cada issue.
 * Não conta como event. Use em transições de fluxo críticas pra dar
 * contexto a erros futuros (busca, seleção, booking, pagamento).
 *
 * @param {string} category - bucket lógico ("booking", "search", "payment", etc.)
 * @param {string} message  - descrição humana do evento
 * @param {object} [data]   - dados extras (manter compacto)
 * @param {'fatal'|'error'|'warning'|'info'|'debug'} [level]
 */
export const breadcrumb = (category, message, data = {}, level = 'info') => {
	if (!initialized) return;
	Sentry.addBreadcrumb({ category, message, data, level });
};

export default {
	initSentry,
	captureError,
	captureMessage,
	setUser,
	clearUser,
	setTag,
	breadcrumb,
	isInitialized,
};
